import { IStorageAdapter } from '../types';
import { StandardStorageKeys } from '../storage/standard-keys';
import { StorageLock, PKCE_LOCK_NAME } from '../storage-lock';

/**
 * PKCE (Proof Key for Code Exchange) Manager
 * Handles code verifier generation, challenge computation, and storage
 *
 * Uses StorageLock to prevent race conditions in multi-tab scenarios
 * when rotating or generating new code verifiers.
 */
export class PKCEManager {
  private readonly storageAdapter: IStorageAdapter;
  private readonly codeVerifierKey: string[];
  private readonly nextCodeVerifierKey: string[];
  private readonly lock: StorageLock | null;
  private readonly memoryOnly: boolean;

  /**
   * Creates a new PKCE Manager instance
   * @param storageAdapter Storage adapter for persisting code verifiers
   * @param codeVerifierKey Storage key for current code verifier (defaults to standard key)
   * @param nextCodeVerifierKey Storage key for next code verifier (defaults to standard key)
   * @param lock Optional StorageLock instance (creates one if not provided, unless memoryOnly is true)
   * @param memoryOnly If true, operates in memory-only mode (no locks)
   */
  constructor(
    storageAdapter: IStorageAdapter,
    codeVerifierKey?: string[],
    nextCodeVerifierKey?: string[],
    lock?: StorageLock,
    memoryOnly = false
  ) {
    this.storageAdapter = storageAdapter;
    this.codeVerifierKey =
      codeVerifierKey || StandardStorageKeys['CODE_VERIFIER'];
    this.nextCodeVerifierKey =
      nextCodeVerifierKey || StandardStorageKeys['NEXT_CODE_VERIFIER'];
    this.memoryOnly = memoryOnly;
    this.lock = memoryOnly ? null : lock || new StorageLock();
  }

  /**
   * Generates a random code verifier
   * @param codeLength Length of the code verifier (default: 43)
   * @returns Generated code verifier string
   */
  public generateCodeVerifier(codeLength = 43): string {
    const array = new Uint8Array(codeLength);
    window.crypto.getRandomValues(array);

    const base64String = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64String;
  }

  /**
   * Generates PKCE challenge from code verifier
   * @param codeVerifier The code verifier to generate challenge from
   * @returns Base64 URL-safe encoded SHA-256 hash
   */
  public async getPkceChallenge(codeVerifier: string): Promise<string> {
    const encoder = new window.TextEncoder();
    const data = encoder.encode(codeVerifier);

    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const base64String = btoa(String.fromCharCode(...hashArray))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64String;
  }

  /**
   * Gets the current and next code verifiers from storage, or generates new ones if not present.
   *
   * @param refresh If true, generates new verifiers regardless of storage
   * @returns Promise resolving to tuple of [currentCodeVerifier, nextCodeVerifier]
   */
  public async getCodeVerifierAsync(
    refresh = false
  ): Promise<[string, string]> {
    if (refresh) {
      if (this.memoryOnly) {
        const codeVerifier = this.generateCodeVerifier(43);
        const nextCodeVerifier = this.generateCodeVerifier(43);
        await this.storageAdapter.setItem(this.codeVerifierKey, codeVerifier);
        await this.storageAdapter.setItem(
          this.nextCodeVerifierKey,
          nextCodeVerifier
        );
        return [codeVerifier, nextCodeVerifier];
      }
      return this.lock!.withLock(PKCE_LOCK_NAME, async () => {
        const codeVerifier = this.generateCodeVerifier(43);
        const nextCodeVerifier = this.generateCodeVerifier(43);
        await this.storageAdapter.setItem(this.codeVerifierKey, codeVerifier);
        await this.storageAdapter.setItem(
          this.nextCodeVerifierKey,
          nextCodeVerifier
        );
        return [codeVerifier, nextCodeVerifier] as [string, string];
      });
    } else {
      const codeVerifier = await this.storageAdapter.getItem(
        this.codeVerifierKey
      );
      const nextCodeVerifier = await this.storageAdapter.getItem(
        this.nextCodeVerifierKey
      );
      if (codeVerifier && nextCodeVerifier) {
        return [codeVerifier, nextCodeVerifier];
      }
      return this.getCodeVerifierAsync(true);
    }
  }

  /**
   * Updates code verifier by rotating: next becomes current, new next is generated.
   * Uses lock for thread safety in multi-tab scenarios.
   *
   * @returns Promise that resolves when rotation is complete
   */
  public async updateCodeVerifierAsync(): Promise<void> {
    if (this.memoryOnly) {
      const codeVerifier = await this.storageAdapter.getItem(
        this.nextCodeVerifierKey
      );
      const nextCodeVerifier = this.generateCodeVerifier(43);
      if (codeVerifier) {
        await this.storageAdapter.setItem(this.codeVerifierKey, codeVerifier);
      }
      await this.storageAdapter.setItem(
        this.nextCodeVerifierKey,
        nextCodeVerifier
      );
      return;
    }
    await this.lock!.withLock(PKCE_LOCK_NAME, async () => {
      const codeVerifier = await this.storageAdapter.getItem(
        this.nextCodeVerifierKey
      );
      const nextCodeVerifier = this.generateCodeVerifier(43);
      if (codeVerifier) {
        await this.storageAdapter.setItem(this.codeVerifierKey, codeVerifier);
      }
      await this.storageAdapter.setItem(
        this.nextCodeVerifierKey,
        nextCodeVerifier
      );
    });
  }

  /**
   * Gets code verifiers and generates challenges with multi-tab safety.
   * Uses lock for thread safety when generating new verifiers.
   *
   * @param refresh If true, generates new verifiers
   * @returns Promise resolving to object with verifiers and challenges
   */
  public async getCodeVerifierAndChallengesAsync(refresh = false): Promise<{
    codeVerifier: string;
    nextCodeVerifier: string;
    codeChallenge: string;
    nextCodeChallenge: string;
  }> {
    const [codeVerifier, nextCodeVerifier] =
      await this.getCodeVerifierAsync(refresh);
    const codeChallenge = await this.getPkceChallenge(codeVerifier);
    const nextCodeChallenge = await this.getPkceChallenge(nextCodeVerifier);

    return {
      codeVerifier,
      nextCodeVerifier,
      codeChallenge,
      nextCodeChallenge,
    };
  }

  /**
   * Clears stored code verifiers with lock for multi-tab safety.
   *
   * @returns Promise that resolves when verifiers are cleared
   */
  public async clearCodeVerifiersAsync(): Promise<void> {
    if (this.memoryOnly) {
      await this.storageAdapter.removeItem(this.codeVerifierKey);
      await this.storageAdapter.removeItem(this.nextCodeVerifierKey);
      return;
    }
    await this.lock!.withLock(PKCE_LOCK_NAME, async () => {
      await this.storageAdapter.removeItem(this.codeVerifierKey);
      await this.storageAdapter.removeItem(this.nextCodeVerifierKey);
    });
  }
}
